import React, { useRef, useState } from 'react';
import { Box, IconButton, InputBase, Typography } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconReusable from '../../../components/IconReusable';

/**
 * Reusable OTP/PIN input component
 * @param {number} length - Number of input boxes
 * @param {string} value - Current value (string of digits)
 * @param {function} onChange - Callback with new value
 * @param {string} type - 'text' or 'password'
 * @param {boolean} showEyeIcon - Show eye icon for show/hide (for PIN)
 */
const BoxInput = ({
  label,
  length = 4,
  value = '',
  onChange,
  type = 'text',
  showEyeIcon = false,
  error,
  boxHeight = 54,
  boxWidth = 64,
  stretchToFit = false,
  disabled = false,
}) => {
  const [show, setShow] = useState(type === 'text');
  const inputs = useRef([]);
  const [focusedIdx, setFocusedIdx] = useState(-1);

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, '');
    if (!val) return;
    let newValue = value.split('');
    newValue[idx] = val[val.length - 1];
    newValue = newValue.join('').padEnd(length, '');
    onChange(newValue);
    if (idx < length - 1 && val) {
      inputs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace') {
      if (!value[idx] && idx > 0) {
        inputs.current[idx - 1]?.focus();
      }
      let newValue = value.split('');
      newValue[idx] = '';
      onChange(newValue.join(''));
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (paste) {
      onChange(paste.padEnd(length, ''));
    }
    e.preventDefault();
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexDirection:'column', }}>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: label ? 'space-between' : 'end' }}>
        {label && (
          <Typography sx={{ color: error ? '#CC0000' : '#666', fontSize: '13px', lineHeight: '20px', fontWeight: '700' }} component="legend">
            {label}
          </Typography>
        )}
        {showEyeIcon && (
          <IconButton
            onClick={() => setShow(s => !s)}
            size="small"
            sx={{ p: 0 }}
          >
          {show ?
            <IconReusable name='Visibility' color={error ? '#CC0000' : '#666'} />  :
            <IconReusable name='VisibilityOff' color={error ? '#CC0000' : '#666'} /> 
          }
          </IconButton>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, width: stretchToFit ? '100%' : 'auto' }}>
      {Array.from({ length }).map((_, idx) => (
        <Box
          key={idx}
          sx={{
            position: 'relative',
            height: boxHeight,
            ...(stretchToFit ? { flex: 1, minWidth: 0 } : { width: boxWidth }),
          }}
        >
          <InputBase
            inputRef={el => (inputs.current[idx] = el)}
            value={value[idx] || ''}
            onChange={e => handleChange(e, idx)}
            onKeyDown={e => handleKeyDown(e, idx)}
            onPaste={handlePaste}
            inputProps={{ 
              maxLength: 1, 
              style: { textAlign: 'center', color: '#995A00' }
            }}
            type={show ? 'text' : 'password'}
            disabled={disabled}
            sx={{
              textAlign: 'center',
              width: '100%',
              height: '100%',
              border: disabled ? '1px solid #D9D9D9' : error ? '1px solid #CC0000' : (value[idx] ? '1px solid #995A00' : '1px solid #D9D9D9'),
              borderRadius: 2,
              background: '#fff',
              fontSize: { xs: '13px', md: '16px' },
              lineHeight: { xs: '20px', md: '24px' },
              fontWeight: 700,
              fontStyle: 'normal',
              transition: 'border 0.2s',
            }}
            onFocus={e => setFocusedIdx(idx)}
            onBlur={e => setFocusedIdx(-1)}
          />
          {!value[idx] && focusedIdx !== idx && (
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#D9D9D9',
            }} />
          )}
        </Box>
      ))}
      </Box>
      
    </Box>
  );
};

export default BoxInput;
